import React from 'react';

export function resolveConfirmDisplay<T>(target: T | null, last: T | null): T | null {
  return target !== null ? target : last;
}

export interface ConfirmTargetControls<T> {
  /** Drives the dialog's `open` prop. */
  open: boolean;
  /** Null when closed; the live target while open. Use for onConfirm logic. */
  target: T | null;
  /**
   * Last non-null target. Use for ALL title/description/copy so the text
   * survives the close animation instead of flashing "undefined".
   */
  display: T | null;
  set: (next: T) => void;
  clear: () => void;
}

export function useConfirmTarget<T>(): ConfirmTargetControls<T> {
  const [target, setTarget] = React.useState<T | null>(null);
  const lastRef = React.useRef<T | null>(null);
  if (target !== null) {
    lastRef.current = target;
  }
  const set = React.useCallback((next: T) => setTarget(next), []);
  const clear = React.useCallback(() => setTarget(null), []);
  return {
    open: target !== null,
    target,
    display: resolveConfirmDisplay(target, lastRef.current),
    set,
    clear,
  };
}
